import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Trash2 } from "lucide-react";
import { api, errorMessage, unwrap } from "../../api/client";
import { Alert, Button, Card, PageHead, Spinner, useToast } from "../../components/ui";
import { num } from "../../lib/format";

export function AdminCache() {
  const qc = useQueryClient();
  const toast = useToast();
  const cache = useQuery({ queryKey: ["provider-cache"], queryFn: () => unwrap(api.GET("/v1/admin/provider-cache")) });
  const clear = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/provider-cache/clear", { body: {} })),
    onSuccess: (r) => {
      toast(r.message);
      qc.invalidateQueries({ queryKey: ["provider-cache"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (cache.isLoading) return <Spinner />;
  if (cache.error || !cache.data) return <Alert tone="danger">{errorMessage(cache.error)}</Alert>;
  const c = cache.data;
  return (
    <>
      <PageHead title="Provider cache" description="Google responses cached on this API server's disk, so re-runs do not re-bill the API." />
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="card stat">
          <div className="label">Status</div>
          <div className="value" style={{ fontSize: 20 }}>{c.enabled ? "Enabled" : "Disabled"}</div>
        </div>
        <div className="card stat">
          <div className="label">Entries</div>
          <div className="value">{num(c.entries)}</div>
        </div>
        <div className="card stat">
          <div className="label">Size</div>
          <div className="value">
            {num(c.size_bytes / 1024 / 1024, 1)}
            <small>MB</small>
          </div>
        </div>
      </div>
      <Card
        title={<span className="row" style={{ gap: 8 }}><Database size={16} /> {c.directory || "No cache directory"}</span>}
        description="Clearing forces fresh (billed) Google requests for the next designs."
        actions={
          <Button
            variant="danger"
            icon={<Trash2 />}
            disabled={!c.entries}
            loading={clear.isPending}
            onClick={() => window.confirm("Clear the whole provider cache?") && clear.mutate()}
          >
            Clear cache
          </Button>
        }
      >
        <p className="muted small">Each API replica has its own cache; this shows the replica that answered.</p>
      </Card>
    </>
  );
}
